import Link from 'next/link';
import { useEffect } from 'react';

import {
  Warrior,
} from '~/../api/common.types';
import VerifyWarriorsWallet from '~/components/auth/VerifyWallet';
import Section from '~/components/hero/LandingPage/Section';
import MainNav from '~/components/navbar/MainNav';
import { WarriorsByTournament } from '~/components/tournament/signup/SignUpWarriorList';
import { OPENSEA_COLLECTION_URL } from '~/constants';
import { ConnectedProviders } from '~/contextProviders/connectedProviders';
import { useConnectedWallet } from '~/contextProviders/ConnectedWallerProvider';
import { useGameAuthentication } from '~/contextProviders/GameAuthenticationProvider';
import { useWarriorAuthentication } from '~/contextProviders/WarriorAuthenticationProvider';
import { useConfirmEmailVerified } from '~/hooks/tournament/useConfirmEmailVerified';
import { useSubmitTournamentRegister } from '~/hooks/tournament/useSubmitTournamentRegister';
import { useGetAccount } from '~/hooks/web3/useGetAccount';
import { shortenedAddress } from '~/utils/utils';

function TournamentRegisterSuccess({
  warriors,
  isLoading,
  isLoadingMore,
  account,
}: {
  warriors: Warrior[] | undefined;
  isLoadingMore: boolean;
  isLoading: boolean;
  account: any;
}) {
  let successMessage = 'Registered, but you do not own any Warriors';
  if (warriors && warriors.length) {
    successMessage = `Success! ${account?.ensName || shortenedAddress(account?.address as `0x${string}`)} is registered`;
  }
  if (isLoading) {
    successMessage = 'Registered. Verifying your Warriors...';
  }
  return (
    <div className="container container--landing-page container--revival">
      <header className="landing-page__header">
        <MainNav showConnectWallet={true} />
      </header>
      <div
        className={`content-wrapper ${
          warriors && warriors.length ? '' : 'content-wrapper--small'
        }`}
      >
        <Section className="landing-page__section--hero">
          <h2 className="landing-page__h2 landing-page__h2--amarante naming-title-spacing">
            {successMessage}
          </h2>
        </Section>
        <div className="battle-nav-tournament-explainer">
          <p className="grid-potions__text grid-potions__explainer">New Tournament every three weeks</p>
          <p className="grid-potions__text grid-potions__explainer">Signup to be automatically enrolled in upcoming tournaments</p>
          <p className="grid-potions__text grid-potions__explainer">Battles every week day, full FAQ and schedule <a className="link" href="https://fictivekin.notion.site/Peacefall-Battle-FAQ-f7d9120ed5774babb34fd95c5f7cc306">here</a></p>
          <p className="grid-potions__text grid-potions__explainer">Additional Warriors can be purchased on <a className="link" href={OPENSEA_COLLECTION_URL}>opensea</a> and will be automatically registered for future tournaments if you use
          this same wallet</p>
        </div>
        {/* <div className="">
          <h3 className="revival-headline">
            Follow on{' '}
            <a
              className="link"
              href="https://twitter.com/peacefallxyz"
              target={'_blank'}
              rel="noreferrer"
            >
              Twitter
            </a>{' '}
            or{' '}
            <a
              className="link"
              href="https://discord.gg/M6MvmVRbuh"
              target={'_blank'}
              rel="noreferrer"
            >
              Discord
            </a>{' '}
            for details. Full FAQ and schedule here
          </h3>
        </div> */}
        {warriors && Boolean(warriors.length) && (
          <WarriorsByTournament warriors={warriors} registered={true} />
        )}
        {/* <div>
          Additional Warriors can be purchased on{' '}
          <a
            className="underline"
            href={OPENSEA_COLLECTION_URL}
          >
            OpenSea
          </a>{' '}
          and will be automatically registered for the tournaments if you use
          this same wallet.
        </div> */}
        {isLoadingMore && (
          <div className="mt-space-xs">Loading more Warriors...</div>
        )}
      </div>
    </div>
  );
}

function TournamentRegisterError() {
  return (
    <div className="container container--landing-page container--revival">
      <header className="landing-page__header">
        <MainNav showConnectWallet={true} />
      </header>
      <div className={'content-wrapper content-wrapper--naming'}>
        <Section className="landing-page__section--hero">
          <h2 className="landing-page__h2 landing-page__h2--amarante naming-title-spacing">
            Enter the Tournaments
          </h2>
        </Section>
        <>
          <h3 className="revival-headline">
            Your email is not verified for this wallet!
          </h3>
          <div>
            <Link href="/tournaments/signup" passHref>
              <button className="warrior__attack__confirm__button primary">
                Verify email
              </button>
            </Link>
          </div>
        </>
      </div>
    </div>
  );
}

const VerifyEmailPage = ({ code }: { code: string | null }) => {
  const { account } = useConnectedWallet();
  const { mutate: verifyEmailMutation, isSuccess, isLoading } = useConfirmEmailVerified();
  useEffect(() => {
    if (code && account?.address && !isSuccess) {
      verifyEmailMutation({
        code: code,
        address: account.address,
      });
    }
  }, [account, account?.address, code, verifyEmailMutation, isSuccess]);

  if (isSuccess) {
    return <VerifyTournamentRegister />;
  }

  return (
    <div className="container container--landing-page container--revival">
      <header className="landing-page__header">
        <MainNav showConnectWallet={true} />
      </header>
      <div className={'content-wrapper content-wrapper--full'}>
        <Section className="landing-page__section--hero">
          <h2 className="landing-page__h2 landing-page__h2--amarante naming-title-spacing">
            Enter the Tournaments
          </h2>
        </Section>
        {isLoading ?
          (
            <div className="mb-space-xs">
              <div>Verifying your email...</div>
            </div>
          ) :
          <>
            <div className="mb-space-xs">
              <div>Connect your wallet to finish email verification!</div>
            </div>
            <div className="name-wallet-connect">
              <VerifyWarriorsWallet />
            </div>
          </>
        }
      </div>
    </div>
  );
};

export const VerifyTournamentRegister = () => {
  const { warriors, hasMore, isLoading, isLoadingMore, loadMore } =
    useWarriorAuthentication();
  const { accessToken, connectedWallet } = useGameAuthentication();
  const { data: account } = useGetAccount();
  const {
    data: tournamentData,
    mutate: submitTournamentMutation,
    isSuccess,
    isError,
  } = useSubmitTournamentRegister();
  useEffect(() => {
    if (accessToken && !isSuccess && connectedWallet) {
      submitTournamentMutation(
        {
          token: accessToken,
          walletAddress: connectedWallet,
        },
        {
          onError: (err: any) => {
            console.log('err', err);
            if (err?.response?.data?.errors) {
              const error = err.response.data['errors'][0];
              if (error?.code === 'tournament:wallet-not-verified') {
                // do nothing (isError will handle)
              } else {
                // eslint-disable-next-line no-alert
                alert(
                  'Something went wrong, please reach out in Discord #dev-tickets'
                );
              }
            }
          },
        }
      );
    }
  }, [accessToken, submitTournamentMutation, isSuccess, connectedWallet]);

  if (isError) {
    return <TournamentRegisterError />;
  }

  if (hasMore && !isLoadingMore) {
    loadMore();
  }

  if (isSuccess && tournamentData && account) {
    return (
      <TournamentRegisterSuccess
        warriors={warriors}
        account={account}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
      />
    );
  }

  return (
    <div className="container container--landing-page container--revival">
      <header className="landing-page__header">
        <MainNav showConnectWallet={true} />
      </header>
      <div className="content-wrapper content-wrapper--naming">
        <Section className="landing-page__section--hero">
          <h2 className="landing-page__h2 landing-page__h2--amarante naming-title-spacing">
            Enter the Tournaments
          </h2>
        </Section>
        <>
          {accessToken ? (
            <>
            </>
          ) : (
            <div className="name-wallet-connect">
              <VerifyWarriorsWallet />
            </div>
          )}
        </>
      </div>
    </div>
  );
};

const Verify = ({ code }: { code: string | null }) => {
  // note: need wallet address, hence making API request client side
  if (code) {
    return (
      <ConnectedProviders accessTokenRequired={true}>
        <VerifyEmailPage code={code} />
      </ConnectedProviders>
    );
  }

  return (
    <ConnectedProviders accessTokenRequired={true}>
      <VerifyTournamentRegister />
    </ConnectedProviders>
  );
};

export default Verify;

export async function getServerSideProps({ query }) {
  return {
    props: { code: query?.code || null },
  };
}
